import { AuthTokens } from "@/model/AuthTokens"
import type { MedicationTaken } from "@/model/BloodGlucoseForm"
import type { BloodGlucoseFormContext } from "@/model/BloodGlucoseFormContext"
import type { QuickCheckReason } from "@/model/QuickCheckReason"
import { atom, getDefaultStore, useAtom } from "jotai"
import { atomWithReset, atomWithStorage, useResetAtom } from "jotai/utils"

// This persists using localStorage internally
const authTokensAtom = atomWithStorage<AuthTokens | null>(
  "tokens",
  null,
  undefined,
  {
    getOnInit: true,
  }
)
export const authTokens = () => getDefaultStore().get(authTokensAtom)
export const setAuthTokens = (tokens: AuthTokens | null) =>
  getDefaultStore().set(authTokensAtom, tokens)
export const useAuthTokensAtom = () => useAtom(authTokensAtom)

export type MealMeasurementState = {
  context: BloodGlucoseFormContext | null
  medicationTaken: MedicationTaken | null
  medicationNote: string
  mealContents: string
  carbsInGrams: string | number
  proteinInGrams: string | number
  notes: string
}

const mealMeasurementInitial: MealMeasurementState = {
  context: null,
  medicationTaken: null,
  medicationNote: "",
  mealContents: "",
  carbsInGrams: "",
  proteinInGrams: "",
  notes: "",
}

const mealMeasurementAtom = atomWithReset<MealMeasurementState>(
  mealMeasurementInitial,
)
export const useMealMeasurement = () => useAtom(mealMeasurementAtom)
export const useResetMealMeasurement = () => useResetAtom(mealMeasurementAtom)

export type QuickCheckState = {
  reasons: QuickCheckReason[]
  notes: string
}

const quickCheckInitial: QuickCheckState = {
  reasons: [],
  notes: "",
}

const quickCheckAtom = atomWithReset<QuickCheckState>(quickCheckInitial)
export const useQuickCheck = () => useAtom(quickCheckAtom)
export const useResetQuickCheck = () => useResetAtom(quickCheckAtom)

export type FoodLogState = {
  dateTime: string | null
  context: BloodGlucoseFormContext | null
  medicationTaken: MedicationTaken | null
  medicationNote: string
  mealContents: string
  carbsInGrams: string | number
  proteinInGrams: string | number
  notes: string
}

const foodLogInitial: FoodLogState = {
  dateTime: null,
  context: null,
  medicationTaken: null,
  medicationNote: "",
  mealContents: "",
  carbsInGrams: "",
  proteinInGrams: "",
  notes: "",
}

const foodLogAtom = atomWithReset<FoodLogState>(foodLogInitial)
export const useFoodLog = () => useAtom(foodLogAtom)
export const useResetFoodLog = () => useResetAtom(foodLogAtom)

export type DeviceCodeInfo = {
  deviceCode: string
  expiresAt: string
}
const deviceCodeInfoAtom = atomWithStorage<DeviceCodeInfo | null>(
  "deviceCodeInfo",
  null,
  undefined,
  { getOnInit: true },
)
export const useDeviceCodeInfo = () => useAtom(deviceCodeInfoAtom)

export type DashboardTab = "overview" | "meal-logs" | "measurement"
const dashboardTabAtom = atom<DashboardTab>("overview")
export const useDashboardTab = () => useAtom(dashboardTabAtom)
